(() => {
    // The section can be added several times on a page and each instance
    // includes this script — only the first copy registers the handler.
    if (window.__dynamicMenuLoaded) return;
    window.__dynamicMenuLoaded = true;

    const debounce = (fn, wait) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), wait);
        };
    };

    class MountingMenu {
        constructor(section, options = {}) {
            this.id = section.id;
            this.container = section.el;
            this.settings = section.data || {};

            this.tablist = this.container.querySelector('.tab-buttons');
            this.buttons = [...this.container.querySelectorAll('.tab-button')];
            this.tabs = [...this.container.querySelectorAll('.tab-content')];
            this.indicator = this.tablist?.querySelector('.tab-indicator') || null;

            this.cellSelector = options.cellSelector || this.settings.cellSelector || '.menu-card';

            this.flickities = new Map();

            // Stable handler refs so they can be removed on section unload.
            this._onButtonClick = this._onButtonClick.bind(this);
            this._lastWidth = 0;
            this._onResize = debounce(() => {
                // Only width matters; Flickity changing the height must not loop.
                const width = this.container.clientWidth;
                if (width === this._lastWidth) return;
                this._lastWidth = width;
                this._refreshAll();
                this._moveIndicator();
            }, 150);

            this.buttons.forEach((button) => button.addEventListener('click', this._onButtonClick));

            // Flickity locks the viewport height when it's built; lazy images
            // (and web fonts) loading afterwards make cards taller and get
            // clipped. Re-measure whenever an image inside the section loads.
            this._onMediaLoad = debounce(() => this._resizeActive(), 50);
            this.container.addEventListener('load', this._onMediaLoad, true);
            document.fonts?.ready.then(this._onMediaLoad);

            // ResizeObserver catches layout changes window 'resize' misses
            // (scrollbar appearing, fonts/images loading, editor panel resize).
            if ('ResizeObserver' in window) {
                this._observer = new ResizeObserver(this._onResize);
                this._observer.observe(this.container);
            } else {
                window.addEventListener('resize', this._onResize);
            }

            this._lastWidth = this.container.clientWidth;
            this._refreshAll();
            this._moveIndicator();
            // Enable the bubble transition only after the first placement so it
            // doesn't slide in from the left on page load.
            requestAnimationFrame(() => this.tablist?.classList.add('is-ready'));
        }

        _onButtonClick(event) {
            this._changeTab(event.currentTarget);
        }

        _changeTab(button) {
            const activeTab = this.container.querySelector(`.${button.dataset.tab}-tab`);

            // Already on this tab (or it doesn't exist) — nothing to do.
            if (!activeTab || activeTab.classList.contains('active')) return;

            this.buttons.forEach((btn) => {
                const isActive = btn === button;
                btn.classList.toggle('active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
            this._moveIndicator();

            // Tear down every carousel before switching: a Flickity left alive
            // inside a display:none tab measures 0 and breaks on the way back.
            this._destroyAll();

            this.tabs.forEach((tab) => tab.classList.toggle('active', tab === activeTab));

            // Build on the next frame, once the new tab is laid out (display:flex)
            // so the wrap detection reads real positions.
            cancelAnimationFrame(this._raf);
            this._raf = requestAnimationFrame(() => this._setupFlickity(activeTab));
        }

        _resizeActive() {
            this.flickities.forEach((flkty, tab) => this._resizeFlickity(tab, flkty));
        }

        _resizeFlickity(tab, flkty) {
            this._equalizeHeights(tab);
            flkty.resize();
        }

        // Carousel cells are absolutely positioned, so CSS can't stretch them
        // to a common height — measure the tallest card and apply it to all.
        _equalizeHeights(tab) {
            const cells = [...tab.querySelectorAll(this.cellSelector)];
            cells.forEach((cell) => { cell.style.minHeight = ''; });
            const tallest = Math.max(...cells.map((cell) => cell.offsetHeight));
            cells.forEach((cell) => { cell.style.minHeight = `${tallest}px`; });
        }

        _clearHeights(tab) {
            tab.querySelectorAll(this.cellSelector).forEach((cell) => { cell.style.minHeight = ''; });
        }

        _destroyAll() {
            this.flickities.forEach((flkty, tab) => {
                flkty.destroy();
                this._clearHeights(tab);
            });
            this.flickities.clear();
        }

        _moveIndicator() {
            if (!this.indicator) return;
            const active = this.buttons.find((btn) => btn.classList.contains('active'));
            if (!active) return;

            // offset* are relative to .tab-buttons (position: relative), so this
            // also works when the buttons wrap onto two rows on small screens.
            const { style } = this.indicator;
            style.width = `${active.offsetWidth}px`;
            style.height = `${active.offsetHeight}px`;
            style.transform = `translate(${active.offsetLeft}px, ${active.offsetTop}px)`;
        }

        _refreshAll() {
            // Only the visible tab ever holds a carousel.
            const activeTab = this.tabs.find((tab) => tab.classList.contains('active'));
            if (activeTab) this._setupFlickity(activeTab);
        }

        _shouldEnableFlickity(tab) {
            // offsetParent is null when the tab (or an ancestor) is display:none.
            if (tab.offsetParent === null) return false;

            const cells = [...tab.querySelectorAll(this.cellSelector)];
            if (cells.length < 2) return false;

            const existing = this.flickities.get(tab);
            if (existing) {
                // Carousel active: cells are absolutely positioned, so compare
                // against the row width recorded when it was enabled.
                return tab.clientWidth < (existing._dmRowWidth || Infinity);
            }

            // Plain flex-wrap layout: any card on a different row than the
            // first one means the list wrapped → switch to the carousel.
            const firstTop = cells[0].offsetTop;
            return cells.some((cell) => Math.abs(cell.offsetTop - firstTop) > 1);
        }

        _rowWidth(tab) {
            const cells = [...tab.querySelectorAll(this.cellSelector)];
            const gap = parseFloat(getComputedStyle(tab).columnGap) || 0;
            return cells.reduce((width, cell) => width + cell.offsetWidth, 0) + gap * (cells.length - 1);
        }

        _setupFlickity(tab) {
            const existing = this.flickities.get(tab);
            const shouldEnable = this._shouldEnableFlickity(tab);

            if (shouldEnable && !existing) {
                if (typeof window.js_default !== 'function') {
                    console.warn('dynamic-menu: Flickity (window.js_default) not available yet.');
                    return;
                }
                const Flickity = window.js_default();
                // Measure before Flickity takes over the layout.
                const rowWidth = this._rowWidth(tab);
                const flkty = new Flickity(tab, {
                    accessibility: false,
                    adaptiveHeight: false,
                    cellSelector: this.cellSelector,
                    contain: true,
                    draggable: true,
                    cellAlign: 'center',
                    friction: 0.5,
                    freeScroll: false,
                    dragThreshold: 10,
                    initialIndex: 0,
                    lazyLoad: false,
                    percentPosition: false,
                    prevNextButtons: !!this.settings.allow_arrows,
                    pageDots: !!this.settings.allow_dots
                });
                flkty._dmRowWidth = rowWidth;
                this.flickities.set(tab, flkty);
                // Re-measure once the .flickity-enabled styles have applied.
                requestAnimationFrame(() => this._resizeFlickity(tab, flkty));
            } else if (!shouldEnable && existing) {
                existing.destroy();
                this._clearHeights(tab);
                this.flickities.delete(tab);
            } else if (shouldEnable && existing) {
                // Visible again after a resize/tab change — recalc geometry.
                existing.reloadCells?.();
                this._resizeFlickity(tab, existing);
            }
        }

        // Called by the Sections API when the section is removed (theme editor).
        onSectionUnload() {
            this._observer?.disconnect();
            window.removeEventListener('resize', this._onResize);
            this.buttons.forEach((button) => button.removeEventListener('click', this._onButtonClick));
            this.container.removeEventListener('load', this._onMediaLoad, true);
            cancelAnimationFrame(this._raf);
            this._destroyAll();
        }
    }

    // Self-contained instancing — no dependency on window.Sections (the
    // minified empire bundle doesn't expose it as a usable constructor).
    const SECTION_TYPE = 'dynamic-menu';
    const instances = new Map();

    const parseData = (script) => {
        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.warn(`dynamic-menu: invalid section data. ${error.message}`);
            return {};
        }
    };

    const mount = (root = document) => {
        root.querySelectorAll(`[data-section-type="${SECTION_TYPE}"][data-section-data]`).forEach((script) => {
            const id = script.dataset.sectionId;
            if (instances.has(id)) return;

            const el = script.closest('.shopify-section') || script.parentElement;
            instances.set(id, new MountingMenu({ id, el, data: parseData(script) }));
        });
    };

    const unmount = (id) => {
        instances.get(id)?.onSectionUnload();
        instances.delete(id);
    };

    // Theme editor: sections are re-rendered in place.
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
    document.addEventListener('shopify:section:unload', (event) => unmount(event.detail.sectionId));

    // Flickity (window.js_default) comes from empire.js, a module script that
    // runs after this deferred one — DOMContentLoaded fires once both ran.
    if (document.readyState === 'complete') {
        mount();
    } else {
        document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
    }
})();
